import React from 'react';

const Step1 = ({ formData, onChange, onFileChange, onRemoveFile, files, errors, onNext, prefilledFields = {} }) => {

    // Helper to determine if a field was auto-populated from database and should be non-editable
    const isReadOnly = (fieldName) => {
        return Boolean(prefilledFields?.[fieldName] || formData?.prefilled_fields?.[fieldName]);
    };

    // Section 1: Customer Information Header & Application Date
    const renderHeaderAndDate = () => (
        <div className="section-head-row">
            <h3 className="section-heading-text">Customer Information:</h3>
            <div className="app-date-wrapper">
                <label htmlFor="application_date">Application Date:</label>
                <input
                    type="date"
                    className={`form-control ${isReadOnly('application_date') ? 'field-readonly' : ''}`}
                    id="application_date"
                    name="application_date"
                    value={formData.application_date || ''}
                    onChange={isReadOnly('application_date') ? undefined : onChange}
                    readOnly={isReadOnly('application_date')}
                    tabIndex={isReadOnly('application_date') ? -1 : 0}
                    required
                />
            </div>
        </div>
    );

    // Section 2: Company Legal Name & Techtop Rep Name
    const renderCompanyAndRep = () => (
        <div className="row form-row-spacing g-2">
            <div className="col-md-7">
                <label className="field-label" htmlFor="company_legal_name">
                    Company full legal name: <span className="text-danger">*</span>
                </label>
                <input
                    type="text"
                    className={`form-control ${errors.company_name ? 'is-invalid border-danger' : ''} ${isReadOnly('company_name') ? 'field-readonly' : ''}`}
                    id="company_legal_name"
                    name="company_name"
                    placeholder="ABC Power Transmission LLC"
                    value={formData.company_name || ''}
                    onChange={isReadOnly('company_name') ? undefined : onChange}
                    readOnly={isReadOnly('company_name')}
                    tabIndex={isReadOnly('company_name') ? -1 : 0}
                    required
                />
                {errors.company_name && (
                    <div className="text-danger small mt-1 fw-bold">{errors.company_name}</div>
                )}
            </div>
            <div className="col-md-5">
                <label className="field-label" htmlFor="techtop_rep_name">Techtop Rep. Name:</label>
                <input
                    type="text"
                    className={`form-control ${isReadOnly('techtop_rep_name') ? 'field-readonly' : ''}`}
                    id="techtop_rep_name"
                    name="techtop_rep_name"
                    placeholder="Joey Fabitz"
                    value={formData.techtop_rep_name || ''}
                    onChange={isReadOnly('techtop_rep_name') ? undefined : onChange}
                    readOnly={isReadOnly('techtop_rep_name')}
                    tabIndex={isReadOnly('techtop_rep_name') ? -1 : 0}
                />
            </div>
        </div>
    );

    // Section 3: Physical & Mailing Addresses, City, County, State, Zip
    const renderAddresses = () => (
        <React.Fragment>
            {/* Physical Address */}
            <div className="row form-row-spacing g-2">
                <div className="col-12">
                    <label className="field-label" htmlFor="physical_address">Physical Address:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('physical_address') ? 'field-readonly' : ''}`}
                        id="physical_address"
                        name="physical_address"
                        placeholder="12345 Highway 23"
                        value={formData.physical_address || ''}
                        onChange={isReadOnly('physical_address') ? undefined : onChange}
                        readOnly={isReadOnly('physical_address')}
                        tabIndex={isReadOnly('physical_address') ? -1 : 0}
                    />
                </div>
            </div>

            {/* Mailing Address */}
            <div className="row form-row-spacing g-2">
                <div className="col-12">
                    <label className="field-label" htmlFor="mailing_address">Mailing Address:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('mailing_address') ? 'field-readonly' : ''}`}
                        id="mailing_address"
                        name="mailing_address"
                        placeholder="P.O. Box 789 or same as physical address"
                        value={formData.mailing_address || ''}
                        onChange={isReadOnly('mailing_address') ? undefined : onChange}
                        readOnly={isReadOnly('mailing_address')}
                        tabIndex={isReadOnly('mailing_address') ? -1 : 0}
                    />
                </div>
            </div>

            {/* City, County, State, Zip */}
            <div className="row form-row-spacing g-2">
                <div className="col-md-3 col-6">
                    <label className="field-label" htmlFor="city_input">City:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('city') ? 'field-readonly' : ''}`}
                        id="city_input"
                        name="city"
                        placeholder="Lawrence"
                        value={formData.city || ''}
                        onChange={isReadOnly('city') ? undefined : onChange}
                        readOnly={isReadOnly('city')}
                        tabIndex={isReadOnly('city') ? -1 : 0}
                    />
                </div>
                <div className="col-md-3 col-6">
                    <label className="field-label" htmlFor="county_input">County:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('county') ? 'field-readonly' : ''}`}
                        id="county_input"
                        name="county"
                        placeholder="Douglas"
                        value={formData.county || ''}
                        onChange={isReadOnly('county') ? undefined : onChange}
                        readOnly={isReadOnly('county')}
                        tabIndex={isReadOnly('county') ? -1 : 0}
                    />
                </div>
                <div className="col-md-3 col-6">
                    <label className="field-label" htmlFor="state_input">State:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('state') ? 'field-readonly' : ''}`}
                        id="state_input"
                        name="state"
                        placeholder="KS"
                        value={formData.state || ''}
                        onChange={isReadOnly('state') ? undefined : onChange}
                        readOnly={isReadOnly('state')}
                        tabIndex={isReadOnly('state') ? -1 : 0}
                    />
                </div>
                <div className="col-md-3 col-6">
                    <label className="field-label" htmlFor="zip_input">Zip:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('zip_code') ? 'field-readonly' : ''}`}
                        id="zip_input"
                        name="zip_code"
                        placeholder="66044"
                        value={formData.zip_code || ''}
                        onChange={isReadOnly('zip_code') ? undefined : onChange}
                        readOnly={isReadOnly('zip_code')}
                        tabIndex={isReadOnly('zip_code') ? -1 : 0}
                    />
                </div>
            </div>
        </React.Fragment>
    );

    // Section 4: Tax ID, Business Tax Exempt Status & Certificate Upload
    const renderTaxExempt = () => (
        <div className="row form-row-spacing g-2 align-items-center">
            <div className="col-md-4 col-sm-6">
                <label className="field-label" htmlFor="tax_id">Tax ID:</label>
                <input
                    type="text"
                    className={`form-control ${isReadOnly('tax_id') ? 'field-readonly' : ''}`}
                    id="tax_id"
                    name="tax_id"
                    placeholder="XX-XXXXXXX"
                    value={formData.tax_id || ''}
                    onChange={isReadOnly('tax_id') ? undefined : onChange}
                    readOnly={isReadOnly('tax_id')}
                    tabIndex={isReadOnly('tax_id') ? -1 : 0}
                />
            </div>
            <div className="col-md-3 col-sm-6">
                <label className="field-label" htmlFor="tax_exempt_status">Business Tax Exempt?:</label>
                <select
                    className={`form-select form-control ${isReadOnly('is_business_tax_exempt') ? 'field-readonly' : ''}`}
                    id="tax_exempt_status"
                    name="is_business_tax_exempt"
                    value={formData.is_business_tax_exempt || 'No'}
                    onChange={isReadOnly('is_business_tax_exempt') ? undefined : onChange}
                    disabled={isReadOnly('is_business_tax_exempt')}
                >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                </select>
            </div>
            <div className="col-md-5 col-12">
                <label className="field-label" htmlFor="tax_exempt_file">
                    (Attach a Copy of <span className="tax-exempt-highlight">tax exempt</span> certificate)
                </label>
                <input
                    type="file"
                    className="form-control"
                    id="tax_exempt_file"
                    name="tax_exempt_file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => onFileChange('tax_exempt_file', e.target.files[0])}
                />
                {files.tax_exempt_file && (
                    <div className="upload-file-display active mt-1">
                        <i className="fa fa-file text-success me-1"></i>
                        <span className="fw-bold">{files.tax_exempt_file.name}</span>
                        <button
                            type="button"
                            className="btn-remove-file ms-auto"
                            onClick={() => onRemoveFile('tax_exempt_file')}
                            title="Remove file"
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // Section 5: Telephone, Fax, Accounts Payable Contact & Email
    const renderContactDetails = () => (
        <React.Fragment>
            {/* Telephone & Fax */}
            <div className="row form-row-spacing g-2">
                <div className="col-md-6 col-12">
                    <label className="field-label" htmlFor="telephone_input">Telephone #:</label>
                    <input
                        type="tel"
                        className={`form-control ${isReadOnly('telephone_number') ? 'field-readonly' : ''}`}
                        id="telephone_input"
                        name="telephone_number"
                        placeholder="(314) 456-7654"
                        value={formData.telephone_number || ''}
                        onChange={isReadOnly('telephone_number') ? undefined : onChange}
                        readOnly={isReadOnly('telephone_number')}
                        tabIndex={isReadOnly('telephone_number') ? -1 : 0}
                    />
                </div>
                <div className="col-md-6 col-12">
                    <label className="field-label" htmlFor="fax_input">Fax#:</label>
                    <input
                        type="tel"
                        className={`form-control ${isReadOnly('fax_number') ? 'field-readonly' : ''}`}
                        id="fax_input"
                        name="fax_number"
                        placeholder="(314) 456-7896"
                        value={formData.fax_number || ''}
                        onChange={isReadOnly('fax_number') ? undefined : onChange}
                        readOnly={isReadOnly('fax_number')}
                        tabIndex={isReadOnly('fax_number') ? -1 : 0}
                    />
                </div>
            </div>

            {/* AP Contact Name & Email */}
            <div className="row form-row-spacing g-2">
                <div className="col-md-6 col-12">
                    <label className="field-label" htmlFor="ap_contact_name">Accounts Payable Contact Name:</label>
                    <input
                        type="text"
                        className={`form-control ${isReadOnly('accounts_payable_contact_name') ? 'field-readonly' : ''}`}
                        id="ap_contact_name"
                        name="accounts_payable_contact_name"
                        placeholder="Jim Stewart"
                        value={formData.accounts_payable_contact_name || ''}
                        onChange={isReadOnly('accounts_payable_contact_name') ? undefined : onChange}
                        readOnly={isReadOnly('accounts_payable_contact_name')}
                        tabIndex={isReadOnly('accounts_payable_contact_name') ? -1 : 0}
                    />
                </div>
                <div className="col-md-6 col-12">
                    <label className="field-label" htmlFor="ap_email_address">E-Mail Address:</label>
                    <input
                        type="email"
                        className={`form-control ${isReadOnly('email_address') ? 'field-readonly' : ''}`}
                        id="ap_email_address"
                        name="email_address"
                        placeholder="ap@abcpower.com"
                        value={formData.email_address || ''}
                        onChange={isReadOnly('email_address') ? undefined : onChange}
                        readOnly={isReadOnly('email_address')}
                        tabIndex={isReadOnly('email_address') ? -1 : 0}
                    />
                </div>
            </div>
        </React.Fragment>
    );

    return (
        <div id="step_1_content">
            {renderHeaderAndDate()}
            {renderCompanyAndRep()}
            {renderAddresses()}
            {renderTaxExempt()}
            {renderContactDetails()}

            {/* Step 1 Next Button */}
            <div className="form-actions-bar">
                <button type="button" className="next-btn" onClick={() => onNext(2)}>
                    Proceed to Step 2: Upload Documents <i className="fa fa-arrow-right ms-2"></i>
                </button>
            </div>
        </div>
    );
};

export default Step1;
